/* eslint-disable react/no-unescaped-entities */
/**
 * FONBEX — 3 logo concepts as React SVG components.
 *
 * Each concept exposes 4 primitives:
 *   - <Symbol />     : glyph only (square)
 *   - <Horizontal /> : glyph + wordmark left-aligned
 *   - <Compact />    : glyph + condensed wordmark for tight header
 *   - <Favicon />    : reduced glyph optimized for 16-32 px favicon
 *
 * All concepts share a common bounding box logic and expose the ``tone`` prop
 * (``dark`` = dark glyph on light bg, ``light`` = light glyph on dark bg,
 * ``mono`` = single-color monochrome).
 *
 * Concepts:
 *   1. Architect  — Monogram F built from stacked horizontal beams (evokes
 *      building floors / lot subdivisions). Precise, structural.
 *   2. Elevation  — Monogram FB drawn with ascending vertical bars, subtle
 *      isometric tilt (evokes portfolio growth, organized assets).
 *   3. Foundation — Solid block using negative space to reveal an F glyph,
 *      projects stability, confidence, structural integrity.
 */
import React from "react";

const PALETTE = {
  primary: "#2454D3",
  night: "#102A56",
  white: "#FFFFFF",
  textPrimary: "#1C2434",
};

const resolveTone = (tone) => {
  if (tone === "light") {
    return { glyph: PALETTE.white, wordmark: PALETTE.white, accent: "rgba(255,255,255,0.6)" };
  }
  if (tone === "mono") {
    return { glyph: PALETTE.textPrimary, wordmark: PALETTE.textPrimary, accent: PALETTE.textPrimary };
  }
  // default: dark on light
  return { glyph: PALETTE.primary, wordmark: PALETTE.night, accent: PALETTE.night };
};

// ============================================================
// CONCEPT 1 — ARCHITECT (Monogram F, stacked beams)
// ============================================================

export const ArchitectSymbol = ({ tone = "dark", size = 64, ...rest }) => {
  const c = resolveTone(tone);
  return (
    <svg viewBox="0 0 64 64" width={size} height={size} role="img"
         aria-label="FONBEX symbole Architecte" {...rest}>
      {/* Vertical spine */}
      <rect x="12" y="8" width="8" height="48" rx="1" fill={c.glyph} />
      {/* Top beam — full width */}
      <rect x="12" y="8" width="40" height="8" rx="1" fill={c.glyph} />
      {/* Middle beam — 2/3 */}
      <rect x="12" y="28" width="28" height="8" rx="1" fill={c.glyph} />
      {/* Baseline mark evoking a lot */}
      <rect x="12" y="52" width="16" height="4" rx="1" fill={c.accent} opacity="0.65" />
    </svg>
  );
};

export const ArchitectHorizontal = ({ tone = "dark", height = 40, ...rest }) => {
  const c = resolveTone(tone);
  return (
    <svg viewBox="0 0 220 64" height={height} role="img"
         aria-label="FONBEX logo horizontal Architecte" {...rest}>
      <ArchitectSymbol tone={tone} size={64} x={0} y={0} />
      <text x="80" y="42" fontFamily="'Cabinet Grotesk', 'Space Grotesk', sans-serif"
            fontWeight="700" fontSize="30" letterSpacing="1"
            fill={c.wordmark}>FONBEX</text>
    </svg>
  );
};

export const ArchitectCompact = (props) => (
  <ArchitectHorizontal {...props} height={props.height || 28} />
);

export const ArchitectFavicon = ({ tone = "dark", size = 32, ...rest }) => {
  const c = resolveTone(tone);
  return (
    <svg viewBox="0 0 32 32" width={size} height={size} role="img"
         aria-label="FONBEX favicon Architecte" {...rest}>
      <rect x="6" y="4" width="4" height="24" rx="0.6" fill={c.glyph} />
      <rect x="6" y="4" width="20" height="4" rx="0.6" fill={c.glyph} />
      <rect x="6" y="14" width="14" height="4" rx="0.6" fill={c.glyph} />
    </svg>
  );
};

// ============================================================
// CONCEPT 2 — ELEVATION (FB monogram, ascending bars, iso tilt)
// ============================================================

export const ElevationSymbol = ({ tone = "dark", size = 64, ...rest }) => {
  const c = resolveTone(tone);
  return (
    <svg viewBox="0 0 64 64" width={size} height={size} role="img"
         aria-label="FONBEX symbole Elevation" {...rest}>
      <g transform="translate(6 8)">
        {/* Ascending vertical bars — evokes a rising portfolio */}
        <rect x="0"  y="30" width="8" height="18" rx="1" fill={c.glyph} opacity="0.85" />
        <rect x="12" y="18" width="8" height="30" rx="1" fill={c.glyph} opacity="0.92" />
        <rect x="24" y="6"  width="8" height="42" rx="1" fill={c.glyph} />
        {/* The B loop hinted as a floating cap on the tallest bar */}
        <path d="M36 8 h8 a6 6 0 0 1 6 6 v6 a6 6 0 0 1 -6 6 h-8 z
                 M36 26 h10 a6 6 0 0 1 6 6 v6 a6 6 0 0 1 -6 6 h-10 z"
              fill={c.accent} opacity="0.9" />
      </g>
    </svg>
  );
};

export const ElevationHorizontal = ({ tone = "dark", height = 40, ...rest }) => {
  const c = resolveTone(tone);
  return (
    <svg viewBox="0 0 240 64" height={height} role="img"
         aria-label="FONBEX logo horizontal Elevation" {...rest}>
      <ElevationSymbol tone={tone} size={64} x={0} y={0} />
      <text x="80" y="42" fontFamily="'Cabinet Grotesk', 'Space Grotesk', sans-serif"
            fontWeight="700" fontSize="30" letterSpacing="1"
            fill={c.wordmark}>FONBEX</text>
    </svg>
  );
};

export const ElevationCompact = (props) => (
  <ElevationHorizontal {...props} height={props.height || 28} />
);

export const ElevationFavicon = ({ tone = "dark", size = 32, ...rest }) => {
  const c = resolveTone(tone);
  return (
    <svg viewBox="0 0 32 32" width={size} height={size} role="img"
         aria-label="FONBEX favicon Elevation" {...rest}>
      <rect x="4"  y="20" width="5" height="8"  rx="0.6" fill={c.glyph} />
      <rect x="12" y="12" width="5" height="16" rx="0.6" fill={c.glyph} />
      <rect x="20" y="4"  width="5" height="24" rx="0.6" fill={c.glyph} />
    </svg>
  );
};

// ============================================================
// CONCEPT 3 — FOUNDATION (Solid block + F revealed by negative space)
// ============================================================

export const FoundationSymbol = ({ tone = "dark", size = 64, ...rest }) => {
  const c = resolveTone(tone);
  return (
    <svg viewBox="0 0 64 64" width={size} height={size} role="img"
         aria-label="FONBEX symbole Foundation" {...rest}>
      {/* Outer solid block */}
      <rect x="4" y="4" width="56" height="56" rx="4" fill={c.glyph} />
      {/* F in negative space */}
      <g fill={tone === "light" ? PALETTE.night : PALETTE.white}>
        <rect x="20" y="14" width="6" height="36" rx="0.5" />
        <rect x="20" y="14" width="24" height="6" rx="0.5" />
        <rect x="20" y="28" width="18" height="6" rx="0.5" />
      </g>
      {/* Diagonal accent cut suggesting foundation stone */}
      <path d="M4 60 L18 60 L4 46 Z" fill={c.accent} opacity="0.75" />
    </svg>
  );
};

export const FoundationHorizontal = ({ tone = "dark", height = 40, ...rest }) => {
  const c = resolveTone(tone);
  return (
    <svg viewBox="0 0 240 64" height={height} role="img"
         aria-label="FONBEX logo horizontal Foundation" {...rest}>
      <FoundationSymbol tone={tone} size={64} x={0} y={0} />
      <text x="80" y="42" fontFamily="'Cabinet Grotesk', 'Space Grotesk', sans-serif"
            fontWeight="700" fontSize="30" letterSpacing="1"
            fill={c.wordmark}>FONBEX</text>
    </svg>
  );
};

export const FoundationCompact = (props) => (
  <FoundationHorizontal {...props} height={props.height || 28} />
);

export const FoundationFavicon = ({ tone = "dark", size = 32, ...rest }) => {
  const c = resolveTone(tone);
  const inner = tone === "light" ? PALETTE.night : PALETTE.white;
  return (
    <svg viewBox="0 0 32 32" width={size} height={size} role="img"
         aria-label="FONBEX favicon Foundation" {...rest}>
      {/* Solid block — no radius at small sizes for crisp pixel edges */}
      <rect x="0" y="0" width="32" height="32" rx={size <= 20 ? 2 : 4} fill={c.glyph} />
      {/* F glyph — thicker strokes for legibility at 16 px */}
      <g fill={inner}>
        <rect x="9"  y="6"  width="5" height="20" />
        <rect x="9"  y="6"  width="14" height="5" />
        <rect x="9"  y="14" width="10" height="5" />
      </g>
    </svg>
  );
};

// ============================================================
// Registry — makes the preview page iterate easily
// ============================================================

export const LOGO_CONCEPTS = [
  {
    id: "architect",
    name: "Architecte",
    tagline: "Monogramme F. Poutres empilées. Structure architecturale et précise.",
    rationale:
      "Chaque poutre horizontale évoque un étage ou un lot. La verticale évoque la colonne portante d'un patrimoine. Utile en très petit format (favicon) car simple et géométrique.",
    Symbol: ArchitectSymbol,
    Horizontal: ArchitectHorizontal,
    Compact: ArchitectCompact,
    Favicon: ArchitectFavicon,
  },
  {
    id: "elevation",
    name: "Elevation",
    tagline: "Monogramme FB. Barres ascendantes. Valorisation et croissance ordonnée.",
    rationale:
      "Trois barres croissantes = portefeuille qui monte, mais organisé, pas spéculatif. Les blocs accent rouge à droite dessinent discrètement un « B », rappel FonBex sans crier.",
    Symbol: ElevationSymbol,
    Horizontal: ElevationHorizontal,
    Compact: ElevationCompact,
    Favicon: ElevationFavicon,
  },
  {
    id: "foundation",
    name: "Foundation",
    tagline: "Bloc plein. F en défonce. Stabilité, confiance, ancrage.",
    rationale:
      "Le bloc massif projette solidité et fiabilité — valeur cardinale de la gestion patrimoniale. Le F en réserve blanche assure une reconnaissance forte, même en 16 px. Le coin taillé rouge apporte une touche contemporaine.",
    Symbol: FoundationSymbol,
    Horizontal: FoundationHorizontal,
    Compact: FoundationCompact,
    Favicon: FoundationFavicon,
  },
];
